import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/layout/Layout';
import { Star, MapPin, ArrowLeft, Award, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CoachProfilePage() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [reviews, setReviews] = useState({ reviews: [], average_rating: null, count: 0 });
  const [form, setForm] = useState({ author_name: '', rating: 5, text: '' });
  const [paying, setPaying] = useState(false);

  const loadReviews = () => axios.get(`${API}/api/coaches/${slug}/reviews`).then(r => setReviews(r.data)).catch(() => {});
  useEffect(() => { axios.get(`${API}/api/coaches/${slug}`).then(r => setC(r.data)).catch(() => setC(false)); loadReviews(); }, [slug]);

  if (c === null) return <Layout><div className="max-w-3xl mx-auto p-8 text-center text-[#94A3B8]">Loading…</div></Layout>;
  if (c === false) return <Layout><div className="max-w-3xl mx-auto p-8 text-center"><h1 className="text-2xl font-bold">Coach not found</h1></div></Layout>;

  const submitReview = async (e) => {
    e.preventDefault();
    if (!form.text.trim()) return toast.error('Write a short review');
    try { await axios.post(`${API}/api/coaches/${slug}/reviews`, form); toast.success('Thanks! Your review is pending approval.'); setForm({ author_name: '', rating: 5, text: '' }); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const payAndBook = async () => {
    setPaying(true);
    const email = prompt('Your email for the booking confirmation:');
    if (!email) { setPaying(false); return; }
    try { const r = await axios.post(`${API}/api/coaches/${slug}/pay`, { email, name: '', origin_url: window.location.origin }); window.location.href = r.data.url; }
    catch (e) { toast.error(e.response?.data?.detail || 'Unavailable'); setPaying(false); }
  };

  return (
    <Layout title={c.name}>
      <div className="max-w-3xl mx-auto px-6 lg:px-8 py-10">
        <Link to="/coaches" className="text-sm text-[#1D4ED8] inline-flex items-center gap-1 mb-6"><ArrowLeft size={14} />All coaches</Link>
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8">
          <div className="flex items-center gap-5 mb-6">
            {c.photo_url ? <img src={c.photo_url} alt={c.name} className="w-24 h-24 rounded-2xl object-cover" /> : <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-[#1D4ED8] to-[#F97316] text-white flex items-center justify-center text-4xl font-bold">{c.name[0]}</div>}
            <div>
              <h1 className="text-3xl font-bold text-[#0A0F1C] flex items-center gap-2" data-testid="coach-name">{c.name}{c.is_featured && <Star size={20} className="text-[#F97316] fill-[#F97316]" />}</h1>
              {c.headline && <p className="text-[#475569] mt-1">{c.headline}</p>}
              <div className="flex items-center gap-4 mt-2 text-sm text-[#94A3B8]">
                {reviews.average_rating != null && <span className="flex items-center gap-1 text-[#F97316] font-semibold"><Star size={14} className="fill-[#F97316]" />{reviews.average_rating} ({reviews.count})</span>}
                {c.location && <span className="flex items-center gap-1"><MapPin size={13} />{c.location}</span>}
                {c.years_experience && <span className="flex items-center gap-1"><Award size={13} />{c.years_experience} yrs</span>}
              </div>
            </div>
          </div>
          {(c.expertise || []).length > 0 && <div className="flex gap-2 flex-wrap mb-6">{c.expertise.map(e => <span key={e} className="text-sm bg-[#EFF6FF] text-[#1D4ED8] px-3 py-1 rounded-full font-medium">{e}</span>)}</div>}
          {c.bio && <p className="text-[#334155] leading-relaxed whitespace-pre-line mb-6">{c.bio}</p>}
          <div className="flex gap-3 flex-wrap">
            {c.session_price > 0 && <button onClick={payAndBook} disabled={paying} className="btn-primary-orange text-sm" data-testid="pay-coach">{paying ? <Loader2 size={14} className="animate-spin inline" /> : `Book a session — $${c.session_price}`}</button>}
            {c.booking_url && <a href={c.booking_url} target="_blank" rel="noreferrer" className="btn-outline text-sm inline-block" data-testid="book-coach">Schedule a call</a>}
          </div>
        </div>

        {/* Reviews */}
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8 mt-6" data-testid="reviews-section">
          <h2 className="text-xl font-bold text-[#0A0F1C] mb-4">Reviews</h2>
          <div className="space-y-3 mb-6">
            {reviews.reviews.map(r => (
              <div key={r.review_id} className="border-b border-[#F1F5F9] pb-3">
                <div className="flex items-center gap-2"><span className="text-[#F97316]">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span><span className="font-semibold text-sm">{r.author_name}</span></div>
                <p className="text-sm text-[#475569] mt-1">{r.text}</p>
              </div>
            ))}
            {reviews.reviews.length === 0 && <p className="text-sm text-[#94A3B8]">No reviews yet — be the first.</p>}
          </div>
          <form onSubmit={submitReview} className="border-t border-[#E2E8F0] pt-4 space-y-2" data-testid="review-form">
            <div className="flex items-center gap-3">
              <input className="px-3 py-2 border rounded text-sm flex-1" placeholder="Your name" value={form.author_name} onChange={e => setForm({ ...form, author_name: e.target.value })} data-testid="review-name" />
              <select value={form.rating} onChange={e => setForm({ ...form, rating: parseInt(e.target.value) })} className="px-3 py-2 border rounded text-sm" data-testid="review-rating">{[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{n} ★</option>)}</select>
            </div>
            <textarea className="w-full px-3 py-2 border rounded text-sm" rows={2} placeholder="Share your experience…" value={form.text} onChange={e => setForm({ ...form, text: e.target.value })} data-testid="review-text" />
            <button className="btn-primary-orange text-sm" data-testid="review-submit">Submit review</button>
          </form>
        </div>
      </div>
    </Layout>
  );
}
